import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader } from '../shared';
import { KbSearchBar, CategoryGrid, ArticleList } from './components';
import { CreateArticleModal } from './components/CreateArticleModal';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { KbCategory, KbArticle } from './callCenterKnowledgeBaseTypes';
import { useKnowledgeBaseStore } from '../../../store/knowledgeBaseStore';
import { KbArticle as BackendArticle } from '../../../services/knowledgeBaseApi';
import styles from './CallCenterKnowledgeBase.module.css';

function adapt(a: BackendArticle): KbArticle {
  return {
    id: String(a.id),
    title: a.title,
    category: a.category,
    updated: new Date(a.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    readTime: `${a.read_time_minutes} min read`,
  };
}

const CallCenterKnowledgeBase: React.FC = () => {
  const items = useKnowledgeBaseStore((s) => s.items);
  const categories = useKnowledgeBaseStore((s) => s.categories);
  const loading = useKnowledgeBaseStore((s) => s.loading);
  const error = useKnowledgeBaseStore((s) => s.error);
  const fetchList = useKnowledgeBaseStore((s) => s.fetchList);
  const fetchCategories = useKnowledgeBaseStore((s) => s.fetchCategories);

  useEffect(() => {
    fetchList();
    fetchCategories();
  }, [fetchList, fetchCategories]);

  const allArticles = useMemo<KbArticle[]>(() => items.map(adapt), [items]);

  const kbCategories = useMemo<KbCategory[]>(
    () => categories.map((c) => ({ id: c.category, name: c.category, articleCount: c.count })),
    [categories]
  );

  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const articles = useMemo<KbArticle[]>(() => {
    const q = query.trim().toLowerCase();
    return allArticles.filter((a) => {
      const matchesCategory = !selectedCategory || a.category === selectedCategory;
      const matchesQuery = !q || a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [allArticles, query, selectedCategory]);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Internal help articles and support procedures"
        buttonLabel="+ New Article"
        onAction={() => setShowCreate(true)}
      />
      <KbSearchBar
        query={query}
        onQueryChange={setQuery}
        selectedCategory={selectedCategory}
        onClearCategory={() => setSelectedCategory(null)}
      />
      <CategoryGrid
        categories={kbCategories}
        selected={selectedCategory}
        onSelect={(name) => setSelectedCategory((prev) => (prev === name ? null : name))}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && articles.length === 0}
        emptyMessage="No knowledge base articles match the current filter"
        onRetry={() => fetchList()}
      >
        <ArticleList articles={articles} />
      </AsyncBoundary>
      <CreateArticleModal
        open={showCreate}
        knownCategories={categories.map((c) => c.category)}
        onClose={() => setShowCreate(false)}
        onCreated={() => { fetchList(); fetchCategories(); }}
      />
    </div>
  );
};

export const CallCenterKnowledgeBasePage: React.FC = () => <CallCenterKnowledgeBase />;

export { CallCenterKnowledgeBase };
export default CallCenterKnowledgeBase;
